import {
    Form
} from "antd";
import { getIdentityDocument } from "../constants/identityDocuments";

export const renderDynamicField = (
    field,
    allFields,
    form,
    countryCode,
    watchedValues
) => {
    const FieldComponent = field.component;

    // 1. Resolver las propiedades dinámicas
    // Si "props" es una función, la ejecutamos pasando el código del país.
    // Si no, usamos directamente el objeto de props o uno vacío por defecto.
    const componentProps = typeof field.props === "function"
        ? field.props(countryCode)
        : field.props || {};

    // 2. Combinar reglas estáticas y dinámicas
    let rules = field.rules ? [...field.rules] : [];

    // Verificar si este campo tiene una validación dinámica configurada
    if (field.dynamicValidator) {
        // Obtenemos el nombre del campo del cual depende la validación dinámica
        const controllingFieldName = field.validationDependency;

        if (!controllingFieldName) {
            // Manejo de error si la dependencia no fue definida en la configuración
            console.error(`Field ${field.name} has a dynamicValidator but is missing a validationDependency in config.`);
        }

        // Ejecutamos la función de validación dinámica, pasando el país, el formulario y el nombre del campo dependiente
        const dynamicRule = field.dynamicValidator(
            countryCode,
            form,
            controllingFieldName // Nombre del campo del que depende
        );
        rules = [...rules, dynamicRule];
    }

    // 3. Determinar si el cambio en este campo afecta la validación de otro campo
    // Buscamos en TODOS los campos si alguno tiene configurado este campo como su dependencia.
    const dependentField = allFields.find(f => f.validationDependency === field.name);

    // Revisar si el campo actual está configurado para limpiar otros campos cuando cambie
    const fieldsToClear = field.clearFieldsOnUpdate || [];

    // Definir el manejador de cambio
    let onChangeHandler = undefined;

    if (dependentField || fieldsToClear.length > 0) {
        // Si otro campo depende de este, debemos disparar la validación de ese campo
        onChangeHandler = (valueOrEvent) => {
            // Ant Design: Select pasa el valor directamente, Input pasa el evento
            const value = valueOrEvent && valueOrEvent.target ? valueOrEvent.target.value : valueOrEvent;

            // 1. Asignar manualmente el valor al campo actual
            form.setFieldValue(field.name, value);

            // 2. Limpiar los campos dependientes
            fieldsToClear.forEach(fieldName => {
                form.setFieldValue(fieldName, undefined);
                form.setFields([{ name: fieldName, errors: [] }]);
            });

            // 3. Revalidar el campo dependiente
            if (dependentField) {
                form.validateFields([dependentField.name]);
            }
        };
    }

    //deshabilitar si dependencia esta vacia
    const dependencyValue = field.validationDependency
        ? watchedValues?.[field.validationDependency]
        : undefined;

    let computedDisabled = false;

    if (field.disableWhenDependencyHasValue && field.validationDependency) {
        computedDisabled = dependencyValue != null && dependencyValue !== "";
    }

    return (
        <Form.Item
            name={field.name}
            label={field.hidden ? undefined : field.label}
            rules={rules}
            style={field.hidden ? { display: "none" } : {}}
        >
            <FieldComponent
                {...componentProps}
                disabled={computedDisabled ?? componentProps.disabled}
                prefix={field.prefix}
                onChange={onChangeHandler}
                type={field.hidden ? "hidden" : componentProps.type}
            />
        </Form.Item>
    );
};

// Map a formato option del componente Select
export const mapToSelectOption = (option) => ({
    label: typeof (option) == "string" ? option : option.label,
    value: typeof (option) == "string" ? option : option.value,
});

export const buildOptions = (arrayStringsOrObjects = []) => {
    if (!Array.isArray(arrayStringsOrObjects)) {
        console.warn("buildOptions espera recibir un array como argumento");
        return [];
    }

    return arrayStringsOrObjects.map(mapToSelectOption);
};

// Función auxiliar para construir dinámicamente las opciones del Select
export const buildDocumentOptions = (country) => {
    const docs = getIdentityDocument(country);
    return Object.values(docs).map((doc) => mapToSelectOption(doc));
};

// Función para crear un validador dinámico a partir de los documentos
export const buildDocumentValidator = (country, form, fieldName) => ({
    validator(_, value) {
        const selectedType = form.getFieldValue(fieldName);
        if (!value) {
            return Promise.reject("Por favor ingrese el número de identificación");
        }

        const docs = getIdentityDocument(country);
        // Encontrar el documento con el "value" que coincida con lo seleccionado
        const doc = Object.values(docs).find((d) =>
            d.value === selectedType || d.aliases?.includes(selectedType)
        );

        if (doc && !doc.regex.test(value)) {
            return Promise.reject(doc.message);
        }

        return Promise.resolve();
    },
});
